import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Toaster from 'components/Toaster';

// Page toasts for Forms: const { showToast, toasterNode } = useFormsToaster().
// Render `toasterNode` once per page. showToast takes
//   { type: 'success' | 'error' | 'info', title, message?, action? }
// (action = Toaster's { text, onClick, icon? }) or a plain string, which
// becomes a success toast with that title.
//
// A toast that must survive navigation rides in router state:
//   history.push(path, { toast: '“Entry form” created under Angell Park' })
// The next page's useFormsToaster shows it once on mount and removes it
// from history, so Back or a refresh doesn't show it again.

export const TOAST_DURATION = 2600;

const normalize = (toast) => {
  if (typeof toast === 'string') return { type: 'success', title: toast };
  const type = toast.type || 'success';
  const fallback = type === 'error' ? 'Something went wrong' : 'Done';
  return { ...toast, type, title: toast.title || toast.message || fallback };
};

const useFormsToaster = () => {
  const history = useHistory();
  const location = useLocation();
  const [toast, setToast] = useState(null);
  const seq = useRef(0);

  const showToast = useCallback((next) => {
    if (!next) return;
    seq.current += 1;
    setToast({ ...normalize(next), key: seq.current });
  }, []);

  const close = useCallback(() => setToast(null), []);

  // Only the state the page arrived with, and only once (a mocked or
  // re-created history object must not replay it).
  const arrival = useRef(location);

  useEffect(() => {
    const landed = arrival.current;
    arrival.current = null;
    const state = landed && landed.state;
    if (!state || !state.toast) return;
    showToast(state.toast);
    const rest = { ...state };
    delete rest.toast;
    history.replace({
      pathname: landed.pathname,
      search: landed.search,
      state: Object.keys(rest).length ? rest : undefined,
    });
  }, [history, showToast]);

  const toasterNode = toast ? (
    <Toaster
      key={toast.key}
      isOpen
      type={toast.type}
      title={toast.title}
      message={toast.title === toast.message ? undefined : toast.message}
      action={toast.action}
      duration={TOAST_DURATION}
      onClose={close}
    />
  ) : null;

  return { showToast, toasterNode };
};

export default useFormsToaster;
